import type { EntityKey } from '../dataSources/types';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import { SESSION_EXPIRED } from './apiErrors';

type ApiContext = {
  baseUrl: string;
  nonce: string;
};

export type ImportJobItemInput = {
  connection_id: string;
  provider_slug: string;
  remote_company_id: string;
  remote_label: string;
  target_company_id?: number;
  allow_create?: boolean;
  include_field_keys?: string[];
  entity_keys?: EntityKey[];
};

export type ImportJobStatus = 'queued' | 'running' | 'completed' | 'partial' | 'failed' | 'cancelled';

export type ImportJobItemStatus =
  | 'queued'
  | 'running'
  | 'success'
  | 'failed'
  | 'skipped'
  | 'needs_user'
  | 'cancelled';

export type ImportJobItem = {
  id: number;
  item_type: 'ensure_mapping' | 'import' | 'finalize_company';
  connection_id: string;
  provider_slug: string;
  remote_company_id: string;
  target_company_id: number | null;
  remote_label: string;
  status: ImportJobItemStatus;
  attempts: number;
  import_session_id: number | null;
  error: { code: string; message: string } | null;
};

export type ImportJobResponse = {
  job_id: number;
  status: ImportJobStatus;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  total_items: number;
  done_items: number;
  failed_items: number;
  error: { code: string; message: string } | null;
  items: ImportJobItem[];
};

const withAuthHeaders = (nonce: string): HeadersInit => ({
  'Content-Type': 'application/json',
  'X-WP-Nonce': nonce,
});

export const createImportJob = async (
  { baseUrl, nonce }: ApiContext,
  items: ImportJobItemInput[],
): Promise<{ job_id: number }> => {
  const response = await fetchWithNonce(`${baseUrl}import-jobs`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: withAuthHeaders(nonce),
    body: JSON.stringify({ items }),
  });

  if (response.status === 401) throw new Error(SESSION_EXPIRED);
  if (!response.ok) throw new Error(await parseApiError(response));

  const payload = (await response.json()) as { job_id?: unknown };
  if (typeof payload.job_id !== 'number' || payload.job_id <= 0) {
    throw new Error('Réponse création job invalide.');
  }

  return { job_id: payload.job_id };
};

export const fetchImportJob = async (
  { baseUrl, nonce }: ApiContext,
  jobId: number,
): Promise<ImportJobResponse> => {
  const response = await fetchWithNonce(`${baseUrl}import-jobs/${jobId}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': nonce },
  });

  if (response.status === 401) throw new Error(SESSION_EXPIRED);
  if (!response.ok) throw new Error(await parseApiError(response));

  return (await response.json()) as ImportJobResponse;
};

export const cancelImportJob = async (
  { baseUrl, nonce }: ApiContext,
  jobId: number,
): Promise<void> => {
  const response = await fetchWithNonce(`${baseUrl}import-jobs/${jobId}/cancel`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: withAuthHeaders(nonce),
    body: JSON.stringify({}),
  });

  if (response.status === 401) throw new Error(SESSION_EXPIRED);
  if (!response.ok) throw new Error(await parseApiError(response));
};
